import { getCase, CASE_STEPS, RECIPIENT_STEP, ATTACHMENTS_STEP, USER_STEP } from "./letterCases";

const formatDate = (value) => {
  if (!value) return "";

  try {
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split("-").map(Number);
      const date = new Date(year, month - 1, day);

      return date.toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }

    const date = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(date.getTime())) return String(value);

    return date.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return String(value);
  }
};

const today = () =>
  new Date().toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const clean = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const lowerFirst = (value) => {
  const text = clean(value);
  if (!text) return "";
  return text.charAt(0).toLowerCase() + text.slice(1);
};

const euro = (value) => {
  const text = clean(value);
  if (!text) return "";
  return `${text} €`;
};

const compact = (text) =>
  text
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const senderAddress = (d) =>
  [d.userFullName, d.userAddress, d.userPostalCity]
    .map(clean)
    .filter(Boolean)
    .join("\n");

const senderContact = (d) =>
  [d.userEmail, d.userPhone].map(clean).filter(Boolean).join(" — ");

const recipientAddress = (d) => {
  const street = d.recipientStreet || d.recipientAddress;
  const postalCity = d.recipientPostalCity || "";

  const addressLines = [d.recipientName, street, postalCity]
    .map(clean)
    .filter(Boolean);

  return addressLines.join("\n");
};

const attachmentsLine = (d) => {
  const attachmentsList = d.attachmentsList || [];
  const otherText = d.attachmentsOther ? clean(d.attachmentsOther) : "";
  const uploadedAttachmentNames = (d.uploadedAttachments || []).map((a) => a.file_name);

  const documentLabels = {
    facture: "Facture",
    confirmation: "Confirmation de commande",
    capture: "Capture d'écran",
    email: "Email de confirmation",
    photo: "Photo",
    contrat: "Contrat",
    paiement: "Justificatif de paiement",
    autre: "Autre document",
  };

  const selectedDocs = attachmentsList
    .map((value) => documentLabels[value] || value)
    .filter(Boolean);

  const legacyNames = d.uploadedAttachmentNames || [];

  const allDocs = Array.from(
    new Set([...selectedDocs, otherText, ...uploadedAttachmentNames, ...legacyNames].filter(Boolean)),
  );

  if (allDocs.length === 0) return "";

  return compact(`Pièces jointes :
${allDocs.map((doc) => `- ${doc}`).join("\n")}`);
};

const signatureBlock = (d) => {
  const parts = [
    `Dans l'attente de votre retour, je vous prie d'agréer, Madame, Monsieur, l'expression de mes salutations distinguées.`,
    clean(d.userFullName),
    (() => {
      const contact = senderContact(d);
      return contact ? `Contact : ${contact}` : null;
    })(),
    (() => {
      const attachments = attachmentsLine(d);
      return attachments || null;
    })(),
  ].filter(Boolean);

  return parts.join("\n\n");
};

const fallbackName = (...values) => {
  const found = values.map(clean).find(Boolean);
  return found || "";
};

const bodies = {
  remboursement: (d) => {
    const merchant = fallbackName(d.merchantName, d.recipientName, "votre établissement");
    const amount = euro(d.amount) || "la somme concernée";
    const purchaseDate = formatDate(d.purchaseDate) || "la date indiquée";
    const orderRef = clean(d.orderRef);
    const reason = clean(d.reason);
    const details = clean(d.details);

    return compact(`Madame, Monsieur,

Je vous adresse la présente au sujet de l'achat effectué le ${purchaseDate} auprès de ${merchant}${orderRef ? `, sous la référence ${orderRef}` : ""}, pour un montant de ${amount}.

${reason ? `Cette demande est liée au motif suivant : ${lowerFirst(reason)}.` : "Malgré mes démarches, la situation n'a pas été régularisée à ce jour."}
${details ? `\n${details}` : ""}

En conséquence, je vous demande de bien vouloir procéder au remboursement de ${amount}, ou de me transmettre une réponse écrite et motivée, dans un délai de quatorze (14) jours à compter de la réception du présent courrier.

À défaut de réponse satisfaisante dans ce délai, je me réserve la possibilité d'engager les démarches utiles auprès des services compétents ou de toute instance appropriée.`);
  },

  logement: (d) => {
    const landlord = fallbackName(d.landlordName, d.recipientName, "votre propriétaire");
    const address = clean(d.propertyAddress) || "l'adresse du logement concerné";
    const issueType = clean(d.issueType);
    const details = clean(d.details);
    const amount = euro(d.amount);
    const leaseStart = formatDate(d.leaseStart);

    return compact(`Madame, Monsieur,

Locataire du logement situé ${address}${leaseStart ? ` depuis le ${leaseStart}` : ""}, je vous adresse la présente au sujet de mon contrat de location, afin de vous signaler ${issueType ? lowerFirst(issueType) : "un litige relatif au logement"}.

${details || "La situation signalée nécessite une régularisation ou une intervention de votre part."}
${amount ? `\nLe montant concerné par ce litige est de ${amount}.` : ""}

Je vous demande de bien vouloir prendre les mesures nécessaires afin de régulariser cette situation dans un délai de quinze (15) jours à compter de la réception du présent courrier.

À défaut de réponse ou de solution satisfaisante, je me réserve la possibilité de saisir les organismes compétents, notamment la commission départementale de conciliation ou la juridiction compétente.`);
  },

  "non-livre": (d) => {
    const merchant = fallbackName(d.merchantName, d.recipientName, "votre service");
    const orderRef = clean(d.orderRef);
    const amount = euro(d.amount) || "la somme versée";
    const purchaseDate = formatDate(d.purchaseDate) || "la date indiquée";
    const expectedDate = formatDate(d.expectedDate);
    const issueType = clean(d.issueType);
    const details = clean(d.details);

    return compact(`Madame, Monsieur,

Le ${purchaseDate}, j'ai passé commande auprès de ${merchant}${orderRef ? ` sous le numéro ${orderRef}` : ""}, pour un montant de ${amount}.

${expectedDate ? `La livraison était prévue le ${expectedDate}.` : "À ce jour, la commande ne m'a pas été livrée dans des conditions satisfaisantes."}
${issueType ? `La situation constatée est la suivante : ${lowerFirst(issueType)}.` : ""}
${details ? `\n${details}` : ""}

Conformément aux règles du Code de la consommation relatives à la livraison des biens et services, notamment les articles L.216-1 et suivants, je vous demande de procéder à la livraison de ma commande dans les meilleurs délais ou, à défaut, de me rembourser l'intégralité des sommes versées.

Sans réponse ou solution satisfaisante dans un délai de sept (7) jours à compter de la réception du présent courrier, je me réserve la possibilité de résoudre la commande et d'engager les démarches nécessaires afin d'obtenir le remboursement des sommes dues.`);
  },

  "mise-en-demeure": (d) => {
    const obligation = clean(d.obligation) || "l'obligation concernée";
    const previousContact = formatDate(d.previousContact);
    const deadlineDays = clean(d.deadlineDays) || "15";
    const details = clean(d.details);

    return compact(`Madame, Monsieur,

${previousContact ? `Faisant suite à mon précédent rappel du ${previousContact},` : "Malgré mes précédentes démarches,"} je constate que vous n'avez toujours pas régularisé la situation suivante :

${obligation}.

${details ? `${details}\n` : ""}Par la présente, je vous demande formellement d'exécuter cette obligation dans un délai de ${deadlineDays} jours à compter de la réception du présent courrier.

À défaut de régularisation dans ce délai, je me réserve la possibilité d'engager toute démarche amiable, administrative ou judiciaire utile à la défense de mes intérêts.`);
  },

  employeur: (d) => {
    const employer = fallbackName(d.employerName, d.recipientName, "votre entreprise");
    const jobTitle = clean(d.jobTitle);
    const contractStart = formatDate(d.contractStart);
    const issueType = clean(d.issueType);
    const details = clean(d.details);
    const amount = euro(d.amount);

    return compact(`Madame, Monsieur,

Dans le cadre de mon contrat de travail avec ${employer}${jobTitle ? `, pour le poste de ${jobTitle}` : ""}${contractStart ? `, débuté le ${contractStart}` : ""}, je vous adresse la présente concernant ${issueType ? lowerFirst(issueType) : "un litige relatif à ma situation professionnelle"}.

${details || "Je souhaite attirer votre attention sur cette situation afin qu'une solution puisse être trouvée dans les meilleurs délais."}
${amount ? `\nLe montant concerné par ma demande est de ${amount}.` : ""}

Je vous demande de bien vouloir examiner ma demande et de m'apporter une réponse écrite dans un délai de quinze (15) jours à compter de la réception de ce courrier.

À défaut de réponse ou de solution satisfaisante, je me réserve la possibilité de solliciter les services compétents, notamment l'inspection du travail, ou de saisir la juridiction prud'homale compétente.`);
  },

  rgpd: (d) => {
    const org = fallbackName(d.orgName, d.recipientName, "votre organisme");
    const requestType = clean(d.requestType) || "ma demande relative à mes données personnelles";
    const accountInfo = clean(d.accountInfo);
    const details = clean(d.details);

    return compact(`Madame, Monsieur,

Je vous adresse la présente afin d'exercer auprès de ${org} mon droit relatif à mes données personnelles, conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi Informatique et Libertés.

Ma demande porte sur : ${requestType}.

${accountInfo ? `L'identifiant ou le compte concerné est le suivant : ${accountInfo}.` : ""}
${details ? `\n${details}` : ""}

Je vous remercie de bien vouloir traiter ma demande dans le délai d'un (1) mois prévu par l'article 12 du RGPD, ou de m'informer dans ce délai si des éléments complémentaires sont nécessaires.

À défaut de réponse dans les délais applicables, je me réserve la possibilité de saisir la Commission Nationale de l'Informatique et des Libertés (CNIL).`);
  },

  consommation: (d) => {
    const merchant = fallbackName(d.merchantName, d.recipientName, "votre établissement");
    const product = clean(d.productName) || "le produit ou service concerné";
    const purchaseDate = formatDate(d.purchaseDate) || "la date indiquée";
    const amount = euro(d.amount);
    const issueType = clean(d.issueType);
    const details = clean(d.details);
    const expectation = clean(d.expectation);

    const expectationMap = {
      reparation: "procéder à la réparation du produit",
      remplacement: "procéder au remplacement à l'identique",
      remboursement: "procéder au remboursement intégral",
    };

    const expectationPhrase = expectationMap[expectation] || "me proposer une solution adaptée";

    return compact(`Madame, Monsieur,

Le ${purchaseDate}, j'ai acquis auprès de ${merchant} le produit ou service suivant : ${product}${amount ? `, pour un montant de ${amount}` : ""}.

${issueType ? `Le problème rencontré est le suivant : ${lowerFirst(issueType)}.` : "Ce produit ou service ne donne pas satisfaction au regard des conditions prévues lors de l'achat."}
${details ? `\n${details}` : ""}

En conséquence, je vous demande de bien vouloir ${expectationPhrase} dans un délai de quinze (15) jours à compter de la réception du présent courrier.

Lorsque le litige concerne la conformité d'un bien, cette demande s'inscrit notamment dans le cadre de la garantie légale de conformité prévue par le Code de la consommation. Lorsque le litige concerne une prestation de service, je vous demande de procéder à la régularisation de la prestation ou de me proposer une solution équivalente.

À défaut de réponse satisfaisante, je me réserve la possibilité de saisir le médiateur de la consommation compétent ou toute autorité appropriée.`);
  },

  voisinage: (d) => {
    const neighborName = clean(d.neighborName);
    const neighborAddress = clean(d.neighborAddress);
    const issueType = clean(d.issueType);
    const frequency = clean(d.frequency);
    const firstIncident = formatDate(d.firstIncident);
    const details = clean(d.details);

    const isNoise =
      issueType.toLowerCase().includes("bruit") ||
      issueType.toLowerCase().includes("tapage");

    return compact(`Madame, Monsieur,

Je vous adresse la présente concernant les troubles de voisinage que je subis${neighborName ? ` de la part de ${neighborName}` : ""}${neighborAddress ? `, en provenance du logement situé ${neighborAddress}` : ""}.

${issueType ? `Nature du trouble : ${lowerFirst(issueType)}.` : ""}
${frequency ? `Fréquence ou horaires constatés : ${frequency}.` : ""}
${firstIncident ? `Premier incident relevé le ${firstIncident}.` : ""}
${details ? `\n${details}` : "Ces nuisances perturbent l'usage normal et paisible de mon logement."}

${isNoise ? "Ces faits sont susceptibles de relever des règles applicables aux nuisances sonores, notamment lorsque leur durée, leur répétition ou leur intensité portent atteinte à la tranquillité du voisinage." : "Ces faits sont susceptibles de constituer un trouble anormal de voisinage lorsqu'ils excèdent les inconvénients normaux liés à la vie en collectivité."}

Je vous demande de bien vouloir prendre les mesures nécessaires afin de faire cesser ces nuisances dans un délai de huit (8) jours à compter de la réception du présent courrier.

À défaut d'amélioration, je me réserve la possibilité de faire constater la situation par les services compétents et d'engager les démarches nécessaires afin de préserver mes droits.`);
  },

  banque: (d) => {
    const bank = fallbackName(d.bankName, d.recipientName, "votre établissement");
    const accountRef = clean(d.accountRef);
    const issueType = clean(d.issueType);
    const operationDate = formatDate(d.operationDate);
    const amount = euro(d.amount);
    const details = clean(d.details);

    return compact(`Madame, Monsieur,

Je suis titulaire d'un compte auprès de ${bank}${accountRef ? `, sous la référence ${accountRef}` : ""}, et je vous adresse la présente afin de contester ${issueType ? lowerFirst(issueType) : "une opération ou une situation relative à mon compte"}.

${operationDate ? `Date de l'opération ou de l'événement concerné : ${operationDate}.` : ""}
${amount ? `Montant concerné : ${amount}.` : ""}
${details ? `\n${details}` : ""}

Je vous demande de bien vouloir procéder à l'examen de ma réclamation et de me transmettre une réponse écrite et motivée dans les meilleurs délais.

Si la situation concerne une opération non autorisée ou une erreur de traitement, je vous remercie de procéder à la régularisation nécessaire conformément aux règles applicables aux services de paiement et à la relation bancaire.

À défaut de réponse satisfaisante, je me réserve la possibilité de saisir le service réclamations de votre établissement, le médiateur bancaire compétent ou toute autorité appropriée.`);
  },

  "caf-reclamation": (d) => {
    const cafNumber = clean(d.cafNumber);
    const benefitType = clean(d.benefitType);
    const issueType = clean(d.issueType);
    const decisionDate = formatDate(d.decisionDate);
    const lastContactDate = formatDate(d.lastContactDate);
    const amount = euro(d.amount);
    const details = clean(d.details);

    const benefitLabels = {
      "Aide au logement / APL": "l'aide au logement / APL",
      "RSA": "le RSA",
      "Prime d’activité": "la prime d'activité",
      "Allocations familiales": "les allocations familiales",
      "AAH": "l'AAH",
      "Autre prestation": "une autre prestation",
    };

    const issueLabels = {
      "Dossier bloqué ou sans réponse": "dossier bloqué ou sans réponse",
      "Paiement suspendu ou non reçu": "paiement suspendu ou non reçu",
      "Document indiqué comme manquant": "document indiqué comme manquant",
      "Demande d’explication": "demande d'explication",
      "Erreur dans mon dossier": "erreur dans mon dossier",
      "Autre situation": "autre situation",
    };

    const benefit = benefitLabels[benefitType] || benefitType || "la prestation concernée";
    const issue = issueLabels[issueType] || issueType || "la situation rencontrée";

    return compact(`Madame, Monsieur,

Je vous adresse la présente réclamation concernant mon dossier CAF${cafNumber ? `, numéro allocataire ${cafNumber}` : ""}.

Cette demande concerne ${benefit} et porte sur la situation suivante : ${issue}.

${decisionDate ? `Le courrier ou la notification concernée date du ${decisionDate}.` : ""}
${lastContactDate ? `Mon dernier échange avec vos services date du ${lastContactDate}.` : ""}
${amount ? `Le montant concerné, à ma connaissance, est de ${amount}.` : ""}

${details || "Je souhaite obtenir une explication claire sur la situation de mon dossier et, le cas échéant, sa régularisation."}

En conséquence, je vous remercie de bien vouloir examiner ma situation, m'indiquer les éléments éventuellement manquants ou les raisons du blocage, et me transmettre une réponse écrite.

Je reste naturellement disponible pour fournir tout document complémentaire nécessaire à l'instruction de mon dossier.

Je vous remercie par avance pour votre aide et pour l'attention portée à ma demande.`);
  },

  "caf-dette": (d) => {
    const cafNumber = clean(d.cafNumber);
    const debtReason = clean(d.debtReason);
    const benefitType = clean(d.benefitType);
    const amount = euro(d.amount) || "le montant indiqué";
    const decisionDate = formatDate(d.decisionDate);
    const requestType = clean(d.requestType);
    const financialSituation = clean(d.financialSituation);
    const proposedMonthlyPayment = euro(d.proposedMonthlyPayment);
    const details = clean(d.details);

    const debtReasonLabels = {
      "Trop-perçu CAF": "un trop-perçu CAF",
      "Demande de remboursement reçue": "une demande de remboursement",
      "Régularisation de droits": "une régularisation de droits",
      "Changement de situation": "un changement de situation",
      "Erreur ou retard de déclaration": "une erreur ou un retard de déclaration",
      "Autre situation": "une autre situation",
    };

    const benefitLabels = {
      "Aide au logement / APL": "l'aide au logement / APL",
      RSA: "le RSA",
      "Prime d’activité": "la prime d'activité",
      "Allocations familiales": "les allocations familiales",
      AAH: "l'AAH",
      "Autre prestation": "une autre prestation",
    };

    const requestLabels = {
      "Remise gracieuse totale": "une remise gracieuse totale de cette dette",
      "Remise gracieuse partielle": "une remise gracieuse partielle de cette dette",
      "Échéancier de paiement": "la mise en place d'un échéancier de paiement adapté à ma situation",
      "Remise partielle et échéancier": "une remise gracieuse partielle ainsi que la mise en place d'un échéancier de paiement adapté",
    };

    const financialLabels = {
      "Revenus faibles": "mes revenus sont actuellement faibles",
      "Charges importantes": "je fais face à des charges importantes",
      "Perte d’emploi ou baisse de revenus": "j'ai subi une perte d'emploi ou une baisse de revenus",
      "Famille ou personnes à charge": "j'ai une famille ou des personnes à charge",
      "Situation personnelle difficile": "je traverse une situation personnelle difficile",
      "Autre situation": "ma situation actuelle ne me permet pas de régler cette somme dans de bonnes conditions",
    };

    const reason = debtReasonLabels[debtReason] || lowerFirst(debtReason) || "une dette ou un trop-perçu";
    const benefit = benefitLabels[benefitType] || benefitType || "la prestation concernée";
    const request = requestLabels[requestType] || lowerFirst(requestType) || "un aménagement de paiement";
    const situation =
      financialLabels[financialSituation] ||
      lowerFirst(financialSituation) ||
      "ma situation financière actuelle est difficile";

    return compact(`Madame, Monsieur,

Je vous adresse la présente concernant mon dossier CAF${cafNumber ? `, numéro allocataire ${cafNumber}` : ""}.

J'ai été informé(e) d'une somme à rembourser de ${amount}, concernant ${benefit}${reason ? `, en lien avec ${reason}` : ""}${decisionDate ? `, à la suite d'un courrier ou d'une notification daté(e) du ${decisionDate}` : ""}.

Sans contester nécessairement l'existence de la demande de remboursement dans ce courrier, je souhaite solliciter ${request}, compte tenu de ma situation actuelle.

En effet, ${situation}.

${details || "Le remboursement immédiat de cette somme représenterait une difficulté importante pour mon foyer. Je souhaite donc qu'une solution adaptée puisse être étudiée."}

${proposedMonthlyPayment ? `Si un échéancier est envisageable, je pourrais proposer un règlement mensuel de ${proposedMonthlyPayment}, sous réserve de l'étude de mon dossier et de mes justificatifs.` : ""}

Je vous remercie de bien vouloir examiner ma demande avec bienveillance et de m'indiquer les justificatifs complémentaires éventuellement nécessaires à son instruction.

Je reste naturellement disponible pour transmettre tout document utile permettant d'apprécier ma situation.

Je vous remercie par avance pour votre aide et pour l'attention portée à ma demande.`);
  },

  "caf-recours": (d) => {
    const cafNumber = clean(d.cafNumber);
    const decisionType = clean(d.decisionType);
    const benefitType = clean(d.benefitType);
    const decisionDate = formatDate(d.decisionDate);
    const amount = euro(d.amount);
    const cafReason = clean(d.cafReason);
    const contestReason = clean(d.contestReason);
    const requestType = clean(d.requestType);

    const decisionLabels = {
      "Refus d’aide ou de prestation": "un refus d'aide ou de prestation",
      "Suppression d’un droit": "une suppression de droit",
      "Baisse d’allocation": "une baisse d'allocation",
      "Trop-perçu contesté": "un trop-perçu que je conteste",
      "Suspension de paiement contestée": "une suspension de paiement que je conteste",
      "Erreur de calcul": "une erreur de calcul",
      "Autre décision": "une autre décision",
    };

    const benefitLabels = {
      "Aide au logement / APL": "l'aide au logement / APL",
      RSA: "le RSA",
      "Prime d’activité": "la prime d'activité",
      "Allocations familiales": "les allocations familiales",
      AAH: "l'AAH",
      "Autre prestation": "une autre prestation",
    };

    const requestLabels = {
      "Réexamen de mon dossier": "le réexamen de mon dossier",
      "Rétablissement de mes droits": "le rétablissement de mes droits",
      "Correction du calcul ou de la situation": "la correction du calcul ou de ma situation",
      "Annulation de la décision contestée": "l'annulation de la décision contestée",
      "Autre demande": "une solution adaptée après réexamen de mon dossier",
    };

    const decision = decisionLabels[decisionType] || lowerFirst(decisionType) || "la décision concernée";
    const benefit = benefitLabels[benefitType] || benefitType || "la prestation concernée";
    const request = requestLabels[requestType] || lowerFirst(requestType) || "le réexamen de mon dossier";

    return compact(`Madame, Monsieur,

Je vous adresse la présente afin de contester la décision concernant mon dossier CAF${cafNumber ? `, numéro allocataire ${cafNumber}` : ""}.

Ce recours porte sur la situation suivante : ${decision} au titre de ${benefit}${decisionDate ? `, notifiée le ${decisionDate}` : ""}${amount ? `, pour un montant de ${amount}` : ""}.

${cafReason ? `Le motif indiqué dans le courrier de la CAF est le suivant : ${cafReason}` : ""}

Je conteste cette décision pour les raisons suivantes :

${contestReason || "Les éléments de mon dossier me semblent nécessiter un réexamen."}

En conséquence, je vous remercie de bien vouloir procéder au réexamen de ma situation et d'étudier ${request}.

Je joins à ce courrier les éléments utiles permettant d'appuyer ma demande et reste naturellement disponible pour transmettre tout justificatif complémentaire nécessaire à l'instruction de mon dossier.

Je vous remercie par avance pour l'attention portée à ma demande.`);
  },
};

const requiredFieldsForCase = (caseId) => {
  const caseSteps = CASE_STEPS[caseId] || [];
  const allSteps = [...caseSteps, RECIPIENT_STEP, ATTACHMENTS_STEP, USER_STEP];

  return allSteps.flatMap((step) =>
    (step.fields || [])
      .filter((field) => field.required)
      .map((field) => field.name),
  );
};

export function validateLetterData(caseId, data = {}) {
  const selectedCase = getCase(caseId);

  if (!selectedCase) {
    return [`unknown-case:${caseId}`];
  }

  const requiredFields = requiredFieldsForCase(caseId);

  return requiredFields.filter((fieldName) => !clean(data[fieldName]));
}

export function buildLetter(caseId, data = {}) {
  const selectedCase = getCase(caseId);

  if (!selectedCase) {
    throw new Error(`Unknown case: ${caseId}`);
  }

  const bodyBuilder = bodies[caseId];

  if (!bodyBuilder) {
    throw new Error(`No template available for case: ${caseId}`);
  }

  const safeData = { ...data };

  const missingFields = validateLetterData(caseId, safeData);

  if (missingFields.length > 0) {
    console.warn(`Missing fields: ${missingFields.join(", ")}`);
    missingFields.forEach((fieldName) => {
      if (!clean(safeData[fieldName])) {
        safeData[fieldName] = `[${fieldName}]`;
      }
    });
  }

  const city = clean(safeData.city);
  const object = clean(selectedCase.objet) || "Demande";
  const orderRef = clean(safeData.orderRef);

  return compact(`${senderAddress(safeData)}

${recipientAddress(safeData)}

${city ? `${city}, le ${today()}` : `Le ${today()}`}

Objet : ${object}${orderRef ? `\nRéf. : ${orderRef}` : ""}

${bodyBuilder(safeData)}

${signatureBlock(safeData)}`);
}